import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useChallenge } from "@/hooks/useChallenge";
import { useStravaConnection } from "@/hooks/useStravaConnection";
import { useStravaConnectionResult } from "@/hooks/useStravaConnectionResult";
import { useHomeDashboard } from "@/hooks/useHomeDashboard";
import { useChallengeStore } from "@/contexts/ChallengeContext";
import { StatsCard } from "@/components/StatsCard";
import { TabNavigation } from "@/components/TabNavigation";
import { MyChallengeCard } from "@/components/MyChallengeCard";
import { DiscoverChallengeCard } from "@/components/DiscoverChallengeCard";
import { CompletedChallengeCard } from "@/components/CompletedChallengeCard";
import { ConnectStravaPrompt } from "@/components/ConnectStravaPrompt";
import { ErrorToast } from "@/components/ErrorToast";
import { ChallengeSummary } from "@/components/ChallengeSummary";
import { ProgressShareModal } from "@/components/ProgressShareModal";
import { VideoModal } from "@/components/VideoModal";
import { StickyHeader } from "@/components/ui/StickyHeader";
import { HeaderIconButton } from "@/components/ui/HeaderIconButton";
import { ChallengeSummaryResponse } from "@/api/challenge";
import {
  getTimeLeft,
  getTimeUntilStart,
  getStatusText,
  calculateProgress,
  getFirstSentence,
  getChallengeIcon,
  isRecentlyCompleted,
} from "@/utils/challengeHelpers";
import { challengeApi } from "@/api/challenge";
import { progressApi } from "@/api/progress";
import { shareApi, ProgressShareData } from "@/api/share";
import { LargeCircularProgress } from "@/components/LargeCircularProgress";
import { YourChallengeStats } from "@/components/YourChallengeStats";
import { StatInfoCard } from "@/components/StatInfoCard";
import { ChallengeTabNavigation } from "@/components/ChallengeTabNavigation";
import { PodiumDisplay } from "@/components/PodiumDisplay";

// ============================================
// MOCK DATA FOR TESTING (DEV ONLY)
// Only visible in development mode (localhost)
// ============================================
// Updated with real user data for: 0x0a06FE7568860983F1225719553f17dF145A6Eaf
// Challenge: Shashank & Co Pilot (0xA417B99451A73a7fA3B32b6c63dcE8eD914D1401)
const MOCK_WINNER_DATA: ChallengeSummaryResponse = {
  challenge: {
    id: "0xA417B99451A73a7fA3B32b6c63dcE8eD914D1401",
    name: "Shashank & Co Pilot",
    type: "daily_distance",
    dailyGoal: 3,
    durationDays: 7,
    requiredDays: 5,
    stakeAmount: 10,
    totalPrizePool: 40,
    startDate: "2024-12-01T00:00:00Z",
    endDate: "2024-12-07T23:59:59Z",
    status: "completed",
  },
  userResult: {
    isWinner: true,
    payoutAmount: 40,
    finalRank: 1,
    totalParticipants: 4,
  },
  personalStats: {
    totalDistance: 31.52,
    totalDuration: 14885,
    totalCalories: 0,
    totalRuns: 3,
    daysCompleted: 3,
    longestRun: 15.2,
    bestDay: { date: "2024-12-05T00:00:00Z", distance: 15.2 },
    averagePace: "7'52\"",
    longestStreak: 3,
  },
  clubStats: {
    totalDistance: 2405,
    totalParticipants: 30,
    totalWinners: 18,
  },
  leaderboard: {
    closestRival: {
      username: "Alex P.",
      avatarUrl: "https://api.dicebear.com/7.x/avataaars/svg?seed=alex",
      distanceAhead: 2.0,
    },
    topPerformers: [
      {
        rank: 1,
        username: "Sarah M.",
        avatarUrl: "https://api.dicebear.com/7.x/avataaars/svg?seed=sarah",
        walletAddress: "0x123",
        totalDistance: 145.2,
        daysCompleted: 30,
        isWinner: true,
      },
      {
        rank: 2,
        username: "Alex P.",
        avatarUrl: "https://api.dicebear.com/7.x/avataaars/svg?seed=alex",
        walletAddress: "0x456",
        totalDistance: 104.4,
        daysCompleted: 30,
        isWinner: true,
      },
      {
        rank: 3,
        username: "You",
        avatarUrl: null,
        walletAddress: "0x789",
        totalDistance: 102.4,
        daysCompleted: 30,
        isWinner: true,
      },
    ],
  },
};

const MOCK_LOSER_DATA: ChallengeSummaryResponse = {
  challenge: {
    id: "mock-challenge-456",
    name: "14-Day Run Challenge",
    type: "daily_distance",
    dailyGoal: 5,
    durationDays: 14,
    requiredDays: 12,
    stakeAmount: 25,
    totalPrizePool: 300,
    startDate: "2024-11-01T00:00:00Z",
    endDate: "2024-11-14T23:59:59Z",
    status: "completed",
  },
  userResult: {
    isWinner: false,
    payoutAmount: 0,
    finalRank: 15,
    totalParticipants: 30,
  },
  personalStats: {
    totalDistance: 84.2,
    totalDuration: 30600,
    totalCalories: 5103,
    totalRuns: 10,
    daysCompleted: 10,
    longestRun: 12.5,
    bestDay: { date: "2024-11-10T00:00:00Z", distance: 12.5 },
    averagePace: "6'12\"",
    longestStreak: 5,
  },
  clubStats: {
    totalDistance: 2405,
    totalParticipants: 30,
    totalWinners: 18,
  },
  leaderboard: {
    closestRival: {
      username: "Alex P.",
      avatarUrl: "https://api.dicebear.com/7.x/avataaars/svg?seed=alex",
      distanceAhead: 3.5,
    },
    topPerformers: [
      {
        rank: 1,
        username: "Sarah M.",
        avatarUrl: "https://api.dicebear.com/7.x/avataaars/svg?seed=sarah",
        walletAddress: "0x123",
        totalDistance: 145.2,
        daysCompleted: 14,
        isWinner: true,
      },
      {
        rank: 2,
        username: "Alex P.",
        avatarUrl: "https://api.dicebear.com/7.x/avataaars/svg?seed=alex",
        walletAddress: "0x456",
        totalDistance: 104.4,
        daysCompleted: 14,
        isWinner: true,
      },
      {
        rank: 15,
        username: "You",
        avatarUrl: null,
        walletAddress: "0x789",
        totalDistance: 84.2,
        daysCompleted: 10,
        isWinner: false,
      },
    ],
  },
};
// Mock data for progress share testing (matches progressshare.png example)
const MOCK_PROGRESS_DATA: ProgressShareData = {
  rank: 7,
  totalParticipants: 30,
  workoutLogged: true,
  prizePool: 1000,
  activityType: "Running",
  distance: 5.21,
  calories: 1324,
  duration: "413",
  pace: "5'23\"",
  completionPercentage: 73,
  daysCompleted: 22,
  totalDays: 30,
  challengeName: "30 Day Running Challenge",
};

// Mock data for Challenge Detail screen testing (Running type)
const MOCK_CHALLENGE_DETAIL_PROGRESS = {
  totalDistance: 86.2,
  distanceGoal: 150, // 5km × 30 days
  averagePace: "5'45\"",
  bestDay: { date: "2024-12-10", distance: 8.5 },
  activeDays: 18,
  totalActiveDays: 22,
  currentDay: 22,
  daysLeft: 8,
  recentActivities: [
    { label: "Today", distance: 5.2, status: "completed" as const, goalMet: true },
    { label: "Yesterday", distance: 4.8, status: "completed" as const, goalMet: false },
    { label: "2 days ago", distance: 5.5, status: "completed" as const, goalMet: true },
  ],
  challengeName: "30-Day Running Streak",
  challengeStatus: "active",
  totalDays: 30,
  stakeAmount: 25,
  totalPrizePool: 750,
  userStakePaid: 25,
};

const MOCK_CHALLENGE_DETAIL_LEADERBOARD = {
  participants: [
    {
      rank: 1,
      walletAddress: "0x1234567890abcdef1234567890abcdef12345678",
      name: "Sarah M.",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=sarah",
      daysCompleted: 22,
      totalRunDistance: 115.5,
      currentStreak: 12,
      isWinner: false,
      isCurrentUser: false,
    },
    {
      rank: 2,
      walletAddress: "0x2345678901bcdef12345678901bcdef123456789",
      name: "Alex P.",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=alex",
      daysCompleted: 21,
      totalRunDistance: 108.2,
      currentStreak: 8,
      isWinner: false,
      isCurrentUser: false,
    },
    {
      rank: 3,
      walletAddress: "0x3456789012cdef123456789012cdef1234567890",
      name: "Mike T.",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=mike",
      daysCompleted: 20,
      totalRunDistance: 98.7,
      currentStreak: 5,
      isWinner: false,
      isCurrentUser: false,
    },
    {
      rank: 4,
      walletAddress: "0x0a06FE7568860983F1225719553f17dF145A6Eaf",
      name: "You",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=you",
      daysCompleted: 18,
      totalRunDistance: 86.2,
      currentStreak: 6,
      isWinner: false,
      isCurrentUser: true,
    },
    {
      rank: 5,
      walletAddress: "0x4567890123def1234567890123def12345678901",
      name: "Emma R.",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=emma",
      daysCompleted: 17,
      totalRunDistance: 82.1,
      currentStreak: 3,
      isWinner: false,
      isCurrentUser: false,
    },
    {
      rank: 6,
      walletAddress: "0x5678901234ef12345678901234ef123456789012",
      name: "David K.",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=david",
      daysCompleted: 15,
      totalRunDistance: 75.3,
      currentStreak: 2,
      isWinner: false,
      isCurrentUser: false,
    },
  ],
  participantCount: 30,
  pagination: null,
  durationDays: 30,
  requiredDays: 25,
  challengeName: "30-Day Running Streak",
  totalDistanceGoal: 150,
};

// ============================================
// END MOCK DATA (DEV ONLY)
// ============================================

export function MyChallenges() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    myActiveChallenges,
    myUpcomingChallenges,
    myCompletedChallenges,
    fetchAllMyChallenges,
    isLoading,
    error,
  } = useChallenge();
  const { showPrompt, ensureConnected, closePrompt, handleConnectionSuccess } =
    useStravaConnection();

  // Handle Strava OAuth callback result (shows toast, cleans URL)
  useStravaConnectionResult();

  const { data: dashboardData } = useHomeDashboard();
  const [activeTab, setActiveTab] = useState<
    "active" | "upcoming" | "completed"
  >("active");

  // ============================================
  // MOCK STATE FOR TESTING (DEV ONLY)
  // ============================================
  const [mockSummaryType, setMockSummaryType] = useState<"winner" | "loser" | null>(null);
  const [mockClaimStatus, setMockClaimStatus] = useState<"CLAIMABLE" | "PENDING_ROOT" | "CLAIMED">("CLAIMABLE");
  const [isDownloadingProgress, setIsDownloadingProgress] = useState(false);
  const [showMockChallengeDetail, setShowMockChallengeDetail] = useState<"running" | null>(null);
  const [mockDetailTab, setMockDetailTab] = useState<"leaderboard" | "rules" | "feed">("leaderboard");
  const isDev = import.meta.env.DEV;
  // ============================================

  // Challenge Summary state (for completed challenges)
  const [summaryData, setSummaryData] = useState<ChallengeSummaryResponse | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [loadingSummaryId, setLoadingSummaryId] = useState<string | null>(null);

  // Progress sync state - tracks which challenge is currently syncing
  const [syncingChallengeId, setSyncingChallengeId] = useState<string | null>(
    null
  );
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState<
    "error" | "warning" | "info" | "success"
  >("success");

  // Progress share modal state
  const [showProgressShare, setShowProgressShare] = useState(false);
  const [progressShareData, setProgressShareData] = useState<ProgressShareData | null>(null);

  // How it works video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Track if we've already fetched to prevent duplicate calls
  const hasFetchedRef = useRef(false);

  // Fetch all my challenges on mount - use direct store access to avoid dependency issues
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      useChallengeStore.getState().fetchAllMyChallenges();
    }
  }, []);

  // Get user's first name or default to "Jane"
  const firstName = user?.username?.split(" ")[0] || "Jane";

  const handleLogActivity = async (challengeId: string) => {
    if (!user?.walletAddress) {
      setToastMessage("Please connect your wallet first");
      setToastVariant("warning");
      setToastOpen(true);
      return;
    }

    setSyncingChallengeId(challengeId);

    try {
      const result = await progressApi.syncProgress(challengeId);

      if (result.success) {
        // Refresh challenges to get updated progress
        await fetchAllMyChallenges();

        // Find the synced challenge from the updated store
        const updatedChallenges = useChallengeStore.getState().myActiveChallenges;
        const syncedChallenge = updatedChallenges.find(c => c.id === challengeId);

        if (syncedChallenge && syncedChallenge.myProgress) {
          const progress = syncedChallenge.myProgress;

          // Calculate pace from duration and distance
          const totalMinutes = Math.floor((progress.totalRunDuration || 0) / 60);
          const totalDistance = progress.totalRunDistance || 0;
          const avgPaceMinutes = totalDistance > 0 ? totalMinutes / totalDistance : 0;
          const paceMin = Math.floor(avgPaceMinutes);
          const paceSec = Math.round((avgPaceMinutes - paceMin) * 60);
          const paceStr = totalDistance > 0 ? `${paceMin}'${paceSec.toString().padStart(2, '0')}"` : "--:--";

          // Build progress share data
          const shareData: ProgressShareData = {
            rank: progress.rank || 1,
            totalParticipants: syncedChallenge.participantCount || 1,
            workoutLogged: true,
            prizePool: syncedChallenge.totalPrizePool || 0,
            activityType: syncedChallenge.type === "daily_distance" ? "Running" : "Activity",
            distance: totalDistance,
            calories: Math.round(totalDistance * 60), // Estimate ~60 cal per km
            duration: totalMinutes.toString(),
            pace: paceStr,
            completionPercentage: calculateProgress(syncedChallenge),
            daysCompleted: progress.daysCompleted || 0,
            totalDays: syncedChallenge.requiredDays || syncedChallenge.durationDays,
            challengeName: syncedChallenge.name,
          };

          setProgressShareData(shareData);
          setShowProgressShare(true);
        } else {
          // Fallback: show toast if we can't get challenge data
          setToastMessage(
            `Synced ${result.activitiesSynced} ${
              result.activitiesSynced === 1 ? "activity" : "activities"
            } from Strava!`
          );
          setToastVariant("success");
          setToastOpen(true);
        }
      } else {
        setToastMessage(result.message || "Failed to sync activities");
        setToastVariant("error");
        setToastOpen(true);
      }
    } catch (error: any) {
      console.error("Error syncing progress:", error);

      // Handle specific error messages from backend
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to sync activities. Please try again.";

      setToastMessage(errorMessage);
      setToastVariant("error");
      setToastOpen(true);
    } finally {
      setSyncingChallengeId(null);
    }
  };

  const handleCreateChallenge = () => {
    // Check if Strava is connected before allowing create
    ensureConnected(() => {
      navigate("/create-challenge");
    });
  };

  const handleProfileClick = () => {
    navigate("/profile");
  };

  // Get challenges based on active tab
  const upcomingChallenges = myUpcomingChallenges;
  const completedChallenges = myCompletedChallenges;

  // Filter recently completed challenges (within last 7 days) for Active tab
  // Only show if user was a participant (has myProgress), not just creator
  const recentlyCompletedChallenges = myCompletedChallenges.filter(
    (challenge) => {
      const isRecent = isRecentlyCompleted(challenge.endDate, 7);
      const hasProgress = !!challenge.myProgress;
      return isRecent && hasProgress;
    }
  );

  // Handle viewing challenge results/summary
  const handleViewResults = async (challengeId: string) => {
    setLoadingSummaryId(challengeId);
    try {
      const summary = await challengeApi.getSummary(challengeId);
      setSummaryData(summary);
      setShowSummary(true);
    } catch (error: any) {
      console.error("Error fetching challenge summary:", error.message);
      setToastMessage(
        error.response?.data?.message || "Failed to load challenge results"
      );
      setToastVariant("error");
      setToastOpen(true);
    } finally {
      setLoadingSummaryId(null);
    }
  };

  // ============================================
  // MOCK PROGRESS SHARE DOWNLOAD (DEV ONLY)
  // ============================================
  const handleDownloadProgressShare = async () => {
    setIsDownloadingProgress(true);
    try {
      const blob = await shareApi.generateProgressShareImage(MOCK_PROGRESS_DATA);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "progress-share-test.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToastMessage("Progress share image downloaded!");
      setToastVariant("success");
      setToastOpen(true);
    } catch (error: any) {
      console.error("Error downloading progress share:", error);
      setToastMessage(error?.response?.data?.error || "Failed to generate progress share image");
      setToastVariant("error");
      setToastOpen(true);
    } finally {
      setIsDownloadingProgress(false);
    }
  };
  // ============================================

  // ============================================
  // MOCK SUMMARY RENDER (DEV ONLY)
  // ============================================
  if (isDev && mockSummaryType) {
    const mockData = mockSummaryType === "winner" ? MOCK_WINNER_DATA : MOCK_LOSER_DATA;
    return (
      <ChallengeSummary
        data={mockData}
        userAvatar={user?.avatarUrl || "https://api.dicebear.com/7.x/avataaars/svg?seed=you"}
        onClose={() => setMockSummaryType(null)}
        onShare={() => {
          // Mock share - just log for now
          console.log("Share clicked");
        }}
        mockClaimStatus={isDev && mockSummaryType === "winner" ? mockClaimStatus : undefined}
        onMockClaim={isDev && mockSummaryType === "winner" ? () => setMockClaimStatus("CLAIMED") : undefined}
      />
    );
  }
  // ============================================
  // END MOCK SUMMARY RENDER (DEV ONLY)
  // ============================================

  // ============================================
  // MOCK CHALLENGE DETAIL RENDER (DEV ONLY)
  // Running type challenge detail preview
  // ============================================
  if (isDev && showMockChallengeDetail) {
    const mockProgress = MOCK_CHALLENGE_DETAIL_PROGRESS;
    const mockLeaderboard = MOCK_CHALLENGE_DETAIL_LEADERBOARD;

    // Get top 3 and rest for leaderboard
    const participants = mockLeaderboard.participants;
    const topThree = participants.slice(0, 3);
    const restOfLeaderboard = participants.slice(3);

    return (
      <div className="relative flex h-auto min-h-screen w-full flex-col bg-background-light dark:bg-background-dark text-content-light dark:text-content-dark">
        {/* Sticky Header */}
        <StickyHeader
          transparent
          leftContent={
            <HeaderIconButton
              icon="arrow_back"
              variant="subtle"
              onClick={() => setShowMockChallengeDetail(null)}
              ariaLabel="Go back"
            />
          }
          title={mockProgress.challengeName}
          rightContent={
            <div className="flex items-center gap-1">
              <span className="text-xs text-primary bg-primary/20 px-2 py-1 rounded-full">
                RUNNING
              </span>
              <HeaderIconButton
                icon="ios_share"
                variant="subtle"
                onClick={() => {}}
                ariaLabel="Share"
              />
            </div>
          }
          className="z-20"
        />

        <main className="flex-1 pb-32">
          {/* Day Progress + Stats */}
          <div className="p-4 pt-6 flex flex-col items-center text-center">
            <LargeCircularProgress
              currentDay={mockProgress.currentDay}
              totalDays={mockProgress.totalDays}
            />
            <p className="mt-4 text-base font-medium text-primary">
              Ends in {mockProgress.daysLeft} days
            </p>
          </div>

          <YourChallengeStats
            userAvatar={user?.avatarUrl || "https://api.dicebear.com/7.x/avataaars/svg?seed=you"}
            totalDistance={mockProgress.totalDistance}
            distanceGoal={mockProgress.distanceGoal}
            averagePace={mockProgress.averagePace}
            bestDay={mockProgress.bestDay}
            activeDays={mockProgress.activeDays}
            totalActiveDays={mockProgress.totalActiveDays}
            recentActivities={mockProgress.recentActivities}
          />

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 px-4 mt-6">
            <StatInfoCard
              label="Total Staked"
              value={`$${mockProgress.totalPrizePool}`}
              icon="emoji_events"
            />
            <StatInfoCard
              label="Your Stake"
              value={`$${mockProgress.userStakePaid}`}
              icon="monetization_on"
            />
          </div>

          {/* Tab Navigation */}
          <ChallengeTabNavigation
            activeTab={mockDetailTab}
            onTabChange={setMockDetailTab}
          />

          {/* Leaderboard */}
          {mockDetailTab === "leaderboard" && (
            <div className="p-4 flex flex-col gap-4">
              {/* Participants Header */}
              <div className="flex items-center justify-between">
                <p className="text-lg font-bold text-content-light dark:text-content-dark">Participants</p>
                <div className="flex items-center justify-start -space-x-3">
                  {participants.slice(0, 3).map((p) => (
                    <div
                      key={p.walletAddress}
                      className="bg-center bg-no-repeat aspect-square bg-cover rounded-full flex items-center justify-center size-8 border-2 border-background-light dark:border-background-dark"
                      style={{ backgroundImage: `url("${p.avatar}")` }}
                    />
                  ))}
                  {mockLeaderboard.participantCount > 3 && (
                    <div className="bg-primary/30 text-primary aspect-square rounded-full flex items-center justify-center size-8 border-2 border-background-light dark:border-background-dark text-xs font-bold">
                      +{mockLeaderboard.participantCount - 3}
                    </div>
                  )}
                </div>
              </div>

              {/* Podium Display */}
              {topThree.length >= 3 && (
                <div className="flex flex-col gap-3">
                  <PodiumDisplay
                    first={{
                      rank: topThree[0].rank,
                      name: topThree[0].name,
                      avatar: topThree[0].avatar,
                      progress: `${topThree[0].daysCompleted}/${mockLeaderboard.requiredDays}`,
                      isWinner: topThree[0].isWinner,
                      walletAddress: topThree[0].walletAddress,
                    }}
                    second={{
                      rank: topThree[1].rank,
                      name: topThree[1].name,
                      avatar: topThree[1].avatar,
                      progress: `${topThree[1].daysCompleted}/${mockLeaderboard.requiredDays}`,
                      walletAddress: topThree[1].walletAddress,
                    }}
                    third={{
                      rank: topThree[2].rank,
                      name: topThree[2].name,
                      avatar: topThree[2].avatar,
                      progress: `${topThree[2].daysCompleted}/${mockLeaderboard.requiredDays}`,
                      walletAddress: topThree[2].walletAddress,
                    }}
                    onParticipantClick={() => {}}
                  />

                  {/* Rest of Leaderboard */}
                  {restOfLeaderboard.length > 0 && (
                    <div className="flex flex-col gap-3 mt-6">
                      {restOfLeaderboard.map((participant) => (
                        <div
                          key={participant.walletAddress}
                          className={`flex items-center gap-3 p-3 rounded-2xl ${
                            participant.isCurrentUser
                              ? "bg-primary/20 border border-primary/40"
                              : "bg-white/5"
                          }`}
                        >
                          <span className="text-lg font-bold text-white/60 w-6">
                            {participant.rank}
                          </span>
                          <div
                            className="size-10 rounded-full bg-cover bg-center"
                            style={{ backgroundImage: `url("${participant.avatar}")` }}
                          />
                          <div className="flex-1">
                            <p className={`font-medium ${participant.isCurrentUser ? "text-primary" : "text-white"}`}>
                              {participant.name}
                            </p>
                            <p className="text-sm text-white/60">
                              {participant.daysCompleted}/{mockLeaderboard.requiredDays}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Rules Content */}
          {mockDetailTab === "rules" && (
            <div className="p-4">
              <div className="rounded-3xl bg-white/5 border border-white/10 p-4">
                <h3 className="text-lg font-bold text-white mb-3">Challenge Rules</h3>
                <ul className="space-y-2 text-white/70">
                  <li className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-lg">check_circle</span>
                    <span>Run at least 5km every day for 30 days</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-lg">check_circle</span>
                    <span>Complete at least 25 days to win</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-lg">check_circle</span>
                    <span>Log your runs via Strava before midnight</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-lg">check_circle</span>
                    <span>Winners split the total prize pool equally</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Feed Content - SHARED */}
          {mockDetailTab === "feed" && (
            <div className="p-4">
              <p className="text-content-light/70 dark:text-content-dark/70 text-center">
                Feed content coming soon...
              </p>
            </div>
          )}
        </main>

        {/* Bottom Action Button - SHARED */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background-light via-background-light to-transparent dark:from-background-dark dark:via-background-dark dark:to-transparent z-10">
          <button
            onClick={() => {}}
            className="flex w-full h-14 items-center justify-center rounded-full bg-primary text-background-dark gap-2 text-base font-bold leading-normal tracking-[0.015em] shadow-glow-primary"
          >
            <span className="material-symbols-outlined">sync</span>
            Sync Progress
          </button>
        </div>
      </div>
    );
  }
  // ============================================
  // END MOCK CHALLENGE DETAIL RENDER (DEV ONLY)
  // ============================================

  // Render real Challenge Summary when viewing completed challenge results
  if (showSummary && summaryData) {
    return (
      <ChallengeSummary
        data={summaryData}
        userAvatar={user?.avatarUrl || "https://api.dicebear.com/7.x/avataaars/svg?seed=user"}
        onClose={() => {
          setShowSummary(false);
          setSummaryData(null);
        }}
        onShare={() => {
          // TODO: Implement share functionality
          console.log("Share clicked");
        }}
      />
    );
  }

  return (
    <div className="relative flex h-dvh w-full flex-col bg-background-light dark:bg-background-dark">
      {/* Fixed Header with safe-area */}
      <StickyHeader
        leftContent={
          <h2 className="text-slate-900 dark:text-white text-lg font-bold leading-tight tracking-[-0.015em]">
            Hello, {firstName}!
          </h2>
        }
        rightContent={
          <HeaderIconButton
            icon="account_circle"
            onClick={handleProfileClick}
            ariaLabel="Profile"
          />
        }
      />

      {/* Stats Section */}
      <div className="flex flex-wrap gap-4 p-4">
        <StatsCard
          label="Total Earnings"
          value={`$${(dashboardData?.stats?.totalEarnings ?? 0).toFixed(2)}`}
        />
        <StatsCard
          label="Total Active Stakes"
          value={`$${(dashboardData?.stats?.totalActiveStakes ?? 0).toFixed(
            2
          )}`}
        />
      </div>

      {/* Tabs Navigation */}
      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Scrollable Tab Content Area */}
      <div className="flex-1 overflow-y-auto pb-24">
        {/* Loading State */}
        {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        /* Error State */
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => fetchAllMyChallenges()}
            className="text-primary underline hover:opacity-80"
          >
            Retry
          </button>
        </div>
      ) : (
        /* Challenge Cards */
        <>
          {/* Active Tab */}
          {activeTab === "active" && (
            <>
              {/* ============================================ */}
              {/* MOCK COMPLETED CHALLENGE (DEV ONLY) */}
              {/* ============================================ */}
              {isDev && (
                <div className="p-4">
                  <div className="rounded-3xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-4 border border-primary/40">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary text-xl">science</span>
                        <span className="text-xs font-bold text-primary uppercase tracking-wider">Summary UI Test</span>
                      </div>
                      <span className="text-xs text-white/50 bg-white/10 px-2 py-1 rounded-full">DEV ONLY</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1">Challenge Summary Preview</h3>
                    <p className="text-sm text-white/60 mb-3">Test the dynamic winner/loser views</p>

                    {/* Claim Status Selector for Winner */}
                    <div className="mb-4">
                      <p className="text-xs text-white/40 mb-2">Winner Claim Status:</p>
                      <div className="flex gap-2">
                        {(["CLAIMABLE", "PENDING_ROOT", "CLAIMED"] as const).map((status) => (
                          <button
                            key={status}
                            onClick={() => setMockClaimStatus(status)}
                            className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                              mockClaimStatus === status
                                ? "bg-primary text-[#0c1c0c]"
                                : "bg-white/5 text-white/60 border border-white/10"
                            }`}
                          >
                            {status === "CLAIMABLE" ? "Claimable" : status === "PENDING_ROOT" ? "Pending" : "Claimed"}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-3 mb-4">
                      <button
                        onClick={() => setMockSummaryType("winner")}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-primary text-[#0c1c0c] font-bold text-sm hover:bg-primary/90 transition-colors"
                      >
                        <span className="material-symbols-outlined text-lg">emoji_events</span>
                        Winner View
                      </button>
                      <button
                        onClick={() => setMockSummaryType("loser")}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-white/10 text-white font-bold text-sm border border-white/20 hover:bg-white/20 transition-colors"
                      >
                        <span className="material-symbols-outlined text-lg">flag</span>
                        Loser View
                      </button>
                    </div>

                    {/* Challenge Detail Test */}
                    <div className="pt-3 border-t border-white/10">
                      <p className="text-xs text-white/40 mb-2">Challenge Detail Screen:</p>
                      <button
                        onClick={() => setShowMockChallengeDetail("running")}
                        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-purple-500/20 text-purple-400 font-bold text-sm border border-purple-500/30 hover:bg-purple-500/30 transition-colors"
                      >
                        <span className="material-symbols-outlined text-lg">directions_run</span>
                        View Running Detail
                      </button>
                      <p className="text-xs text-white/30 mt-2 text-center">
                        Day {MOCK_CHALLENGE_DETAIL_PROGRESS.currentDay}/{MOCK_CHALLENGE_DETAIL_PROGRESS.totalDays}
                      </p>
                    </div>

                    {/* Progress Share Test */}
                    <div className="pt-3 border-t border-white/10">
                      <p className="text-xs text-white/40 mb-2">Progress Share Template:</p>
                      <button
                        onClick={handleDownloadProgressShare}
                        disabled={isDownloadingProgress}
                        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-blue-500/20 text-blue-400 font-bold text-sm border border-blue-500/30 hover:bg-blue-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isDownloadingProgress ? (
                          <>
                            <span className="animate-spin material-symbols-outlined text-lg">progress_activity</span>
                            Generating...
                          </>
                        ) : (
                          <>
                            <span className="material-symbols-outlined text-lg">download</span>
                            Download Progress Share
                          </>
                        )}
                      </button>
                      <p className="text-xs text-white/30 mt-2 text-center">
                        Rank #{MOCK_PROGRESS_DATA.rank} | {MOCK_PROGRESS_DATA.daysCompleted}/{MOCK_PROGRESS_DATA.totalDays} Days | ${MOCK_PROGRESS_DATA.prizePool} pool
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* ============================================ */}
              {/* END MOCK COMPLETED CHALLENGE (DEV ONLY) */}
              {/* ============================================ */}

              {/* Active Challenges */}
              {myActiveChallenges.length > 0 &&
                myActiveChallenges.map((challenge, index) => (
                  <div
                    key={challenge.id}
                    className={index === 0 ? "p-4" : "px-4 pb-4"}
                  >
                    {challenge.myProgress ? (
                      <MyChallengeCard
                        title={challenge.name}
                        stakeAmount={`$${challenge.stakeAmount}`}
                        progress={calculateProgress(challenge)}
                        timeLeft={getTimeLeft(challenge.endDate)}
                        statusText={getStatusText(challenge.status, 0)}
                        onLogActivity={() => handleLogActivity(challenge.id)}
                        onClick={() =>
                          navigate(`/my-challenge/${challenge.id}`)
                        }
                        isSyncing={syncingChallengeId === challenge.id}
                      />
                    ) : (
                      <DiscoverChallengeCard
                        title={challenge.name}
                        description={getFirstSentence(challenge.description)}
                        icon={getChallengeIcon(challenge.type)}
                        duration={`${challenge.durationDays} Days`}
                        stake={`$${challenge.stakeAmount}`}
                        players={`${challenge.participantCount || 0}`}
                        onClick={() =>
                          navigate(`/challenge-preview/${challenge.id}`)
                        }
                        variant="active"
                      />
                    )}
                  </div>
                ))}

              {/* Upcoming Challenges (shown in Active tab) */}
              {upcomingChallenges.length > 0 &&
                upcomingChallenges.map((challenge, index) => (
                  <div
                    key={challenge.id}
                    className={
                      myActiveChallenges.length === 0 && index === 0
                        ? "p-4"
                        : "px-4 pb-4"
                    }
                  >
                    <MyChallengeCard
                      title={challenge.name}
                      stakeAmount={`$${challenge.stakeAmount}`}
                      progress={0}
                      timeLeft={getTimeUntilStart(challenge.startDate)}
                      statusText="Starting soon"
                      isUpcoming={true}
                      startsIn={getTimeUntilStart(challenge.startDate)}
                      onClick={() =>
                        navigate(`/challenge-preview/${challenge.id}`)
                      }
                    />
                  </div>
                ))}

              {/* Recently Completed Challenges (last 7 days) */}
              {recentlyCompletedChallenges.length > 0 && (
                <>
                  <div className="px-4 pt-2 pb-2">
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Recently Completed
                    </h3>
                  </div>
                  {recentlyCompletedChallenges.map((challenge) => (
                    <div key={challenge.id} className="px-4 pb-4">
                      <CompletedChallengeCard
                        title={challenge.name}
                        stakeAmount={`$${challenge.stakeAmount}`}
                        isWinner={challenge.myProgress?.isWinner ?? false}
                        payoutAmount={challenge.myProgress?.payoutAmount ?? 0}
                        daysCompleted={challenge.myProgress?.daysCompleted ?? 0}
                        requiredDays={challenge.requiredDays}
                        onClick={() => handleViewResults(challenge.id)}
                        isLoading={loadingSummaryId === challenge.id}
                      />
                    </div>
                  ))}
                </>
              )}

              {/* Empty state - only show if all are empty */}
              {myActiveChallenges.length === 0 &&
                upcomingChallenges.length === 0 &&
                recentlyCompletedChallenges.length === 0 && (
                  <div className="flex flex-col px-4 pt-4">
                    {/* Journey Steps */}
                    <div className="flex flex-col gap-4 mb-6">
                      <h2 className="text-slate-900 dark:text-white text-lg font-bold leading-tight tracking-[-0.015em] text-center">
                        Your Journey Starts Here
                      </h2>
                      <div className="flex flex-col gap-3">
                        {[
                          {
                            number: 1,
                            title: "Create or Discover a Challenge",
                            description:
                              "Create a run challenge to ignite your run goals with your friends.",
                            icon: "search",
                            iconFilled: false,
                          },
                          {
                            number: 2,
                            title: "Pledge Your Money",
                            description:
                              "Add some skin in the game. Consistency pays.",
                            icon: "payments",
                            iconFilled: true,
                          },
                          {
                            number: 3,
                            title: "Stay Consistent",
                            description:
                              "Complete your goal, win your money back, and more!",
                            icon: "local_fire_department",
                            iconFilled: true,
                          },
                        ].map((step) => (
                          <div
                            key={step.number}
                            className="flex items-start gap-4 p-4 rounded-3xl bg-white dark:bg-black/30 border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none"
                          >
                            <span className="text-lg font-bold text-primary">
                              {step.number}.
                            </span>
                            <div className="flex-1">
                              <h3 className="font-bold text-slate-900 dark:text-white">
                                {step.title}
                              </h3>
                              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                {step.description}
                              </p>
                            </div>
                            <div className="flex items-center justify-center size-10 rounded-full bg-primary/10 border border-primary/20">
                              <span
                                className="material-symbols-outlined text-primary text-2xl"
                                style={
                                  step.iconFilled
                                    ? {
                                        fontVariationSettings:
                                          "'FILL' 1, 'wght' 400",
                                      }
                                    : undefined
                                }
                              >
                                {step.icon}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex flex-col gap-3 py-4">
                      <button
                        onClick={() => navigate("/challenges")}
                        className="flex h-14 w-full cursor-pointer items-center justify-center overflow-hidden rounded-full bg-primary px-5 text-base font-bold leading-normal tracking-[0.015em] text-slate-900 transition-transform hover:scale-105"
                      >
                        <span className="truncate">Browse Challenges</span>
                      </button>
                      <button
                        onClick={handleCreateChallenge}
                        className="flex h-14 w-full cursor-pointer items-center justify-center overflow-hidden rounded-full border border-slate-400/50 dark:border-white/20 bg-transparent dark:bg-white/10 px-5 text-base font-bold leading-normal tracking-[0.015em] text-slate-900 dark:text-white transition-transform hover:scale-105"
                      >
                        <span className="truncate">Create Your Own</span>
                      </button>
                    </div>

                    {/* How Dasher Works Link */}
                    <div className="py-4">
                      <button
                        onClick={() => setShowVideoModal(true)}
                        className="w-full flex items-center justify-between gap-4 rounded-3xl p-4 bg-slate-100 dark:bg-black/20 border border-slate-200 dark:border-white/10 hover:bg-slate-200 dark:hover:bg-black/40 transition-all duration-200 min-h-[72px] hover:scale-[1.02] active:scale-[0.98] active:opacity-80"
                      >
                        <div className="flex-1">
                          <h4 className="text-slate-900 dark:text-white font-bold">
                            How Dasher Works
                          </h4>
                          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                            Learn how to turn consistency into cash.
                          </p>
                        </div>
                        <span
                          className="material-symbols-outlined text-primary"
                          style={{
                            fontVariationSettings:
                              "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 48",
                            fontSize: "32px",
                          }}
                        >
                          play_circle
                        </span>
                      </button>
                    </div>
                  </div>
                )}
            </>
          )}

          {/* Upcoming Tab - Use DiscoverChallengeCard */}
          {activeTab === "upcoming" && (
            <div className="flex flex-col gap-4 px-4 pt-4">
              {upcomingChallenges.length > 0 ? (
                upcomingChallenges.map((challenge) => (
                  <DiscoverChallengeCard
                    key={challenge.id}
                    title={challenge.name}
                    description={getFirstSentence(challenge.description)}
                    icon={getChallengeIcon(challenge.type)}
                    duration={`${challenge.durationDays} Days`}
                    stake={`$${challenge.stakeAmount}`}
                    players={`${
                      challenge.participantCount ||
                      challenge.participants?.length ||
                      0
                    }`}
                    onClick={() =>
                      navigate(`/challenge-preview/${challenge.id}`)
                    }
                    variant="active"
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <p className="text-slate-500 dark:text-slate-400 text-lg">
                    No upcoming challenges
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Completed Tab */}
          {activeTab === "completed" && (
            <div className="flex flex-col gap-4 px-4 pt-4">
              {completedChallenges.filter((c) => c.myProgress).length > 0 ? (
                completedChallenges
                  .filter((c) => c.myProgress)
                  .map((challenge) => (
                    <CompletedChallengeCard
                      key={challenge.id}
                      title={challenge.name}
                      stakeAmount={`$${challenge.stakeAmount}`}
                      isWinner={challenge.myProgress?.isWinner ?? false}
                      payoutAmount={challenge.myProgress?.payoutAmount ?? 0}
                      daysCompleted={challenge.myProgress?.daysCompleted ?? 0}
                      requiredDays={challenge.requiredDays}
                      onClick={() => handleViewResults(challenge.id)}
                      isLoading={loadingSummaryId === challenge.id}
                    />
                  ))
              ) : (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <p className="text-slate-500 dark:text-slate-400 text-lg">
                    No completed challenges yet
                  </p>
                </div>
              )}
            </div>
          )}
        </>
        )}
      </div>

      {/* Floating Action Button */}
      <div className="fixed bottom-26 right-6 z-20 md:bottom-6">
        <button
          onClick={handleCreateChallenge}
          className="flex h-14 w-14 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-primary text-slate-900 shadow-lg hover:scale-105 transition-transform"
        >
          <span className="material-symbols-outlined text-3xl">add</span>
        </button>
      </div>

      {/* Strava Connection Prompt */}
      <ConnectStravaPrompt
        isOpen={showPrompt}
        onClose={closePrompt}
        onSuccess={handleConnectionSuccess}
      />

      {/* Progress Share Modal - shown after successful activity sync */}
      <ProgressShareModal
        isOpen={showProgressShare}
        onClose={() => {
          setShowProgressShare(false);
          setProgressShareData(null);
        }}
        data={progressShareData}
      />

      {/* How It Works Video Modal */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoId="6sygIKvdtzM"
        title="How Dasher Works"
      />

      {/* Toast Notification */}
      <ErrorToast
        isOpen={toastOpen}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        variant={toastVariant}
        position="top"
        duration={5000}
      />
    </div>
  );
}
